import React, {useState, useEffect, useRef} from 'react';
import {
  Flex,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Td,
  Table,
  TableCaption,
  Tfoot,
  IconButton,
  useDisclosure,
  Divider,
  Box,
  Spacer,
} from '@chakra-ui/react';
import LoadingWheel from '../../components/General/LoadingWheel';
import {get, postDelete, queryStringBuilder} from '../../service/ApiService';
import {SMaster} from '../../service/Urls';
import {FaTrashAlt, FaEdit, FaSearch} from 'react-icons/fa';
import {useLocation, useNavigate} from 'react-router-dom';
import ModalYesNo from '../../components/General/ModalYesNo';
import {setGrid, setSearchHeaders} from '../../helpers/SetGrid';
import ErrorHelper from '../../service/ErrorHelper';
import BoxCard from '../../components/General/BoxCard';
import {SubmitButton} from '../../components/General/FormInputs';
import {useForm} from 'react-hook-form';
import ServiceTypeList from '../../components/General/ServiceTypeList';
import {useTitle} from '../../components/General/Layout';
import {isAdmin} from '../../helpers/localStorage';

const MasterMaintenance = ({isException}) => {
  const [masterData, setMasterData] = useState([]);
  const [toastResponse, setToastResponse] = useState(undefined);
  const [searchHeaders, setHeaders] = useState({});

  const [forDelete, setForDelete] = useState({id: '', masterName: ''});
  const [elements, setElements] = useState({body: {}, header: {}, footer: {}});
  const [isLoading, setLoading] = useState(true);
  const [serviceType, setServiceType] = useState(1);
  const [title, setTitle] = useTitle('Master Maintenance - Search');
  const [isSalesMan, setIsSalesMan] = useState(true);

  const location = useLocation();

  const [pages, setPages] = useState({
    pageNum: 1,
    totalPages: 5,
    itemsPerPage: 5,
    getNewPage: curPage => {
      loadData(curPage);
    },
  });

  const navigate = useNavigate();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
  } = useForm();
  // useRef will save this across renders so it's not reloaded every time
  // to add more fields to the table, simply add their name and type to this json
  // if a new type is added you'll have to update the table to reflect that type (ie. dropdown or button)
  const visibleData = useRef({
    recordNumber: 'text',
    contractNumber: 'text',
    buyerFirstName: 'text',
    buyerLastName: 'text',
    deceasedFirstName: 'text',
    deceasedLastName: 'text',
    contractDate: 'date',
  });

  // What the id's and types of the formdata should be that is sent to the api when posting
  const searchIds = useRef({
    RecordNumber: 'number',
    ContractNumber: 'text',
    BuyerFirstName: 'text',
    BuyerLastName: 'text',
    DeceasedFirstName: 'text',
    DeceasedLastName: 'text',
    ContractDate: 'date',
  });

  // Initial load of the page
  useEffect(() => {
    if (isAdmin()) setIsSalesMan(false);
    loadData(1);
  }, []);

  const loadData = async () => {
    const svcType = location?.state?.serviceType.value ?? 1;
    setServiceType(svcType);
    // initial search values
    const queryParams = {
      Page: 1,
      PageSize: 10,
      ServiceType: svcType,
      OrderBy: 'ContractDate',
      IsDescendingSort: true,
    };
    queryData(queryParams);
  };

  useEffect(() => {
    loadTitle();
  }, []);

  const loadTitle = () => {
    setTitle(isException ? 'Exception Maintenance - Search' : 'Master Maintenance - Search');
  };

  // Need this to repopulate everything on the UI any time the data from the web changes
  useEffect(() => {
    masterData.map(record => {
      if (record.contractDate) {
        record.contractDate = record.contractDate.replaceAll('-', '/');
        record.contractDate = record.contractDate.substring(0, record.contractDate.indexOf('T'));
      }
    });
    populateData();
  }, [masterData]);

  // if no masterId is passed in, we'll assume we're adding.
  const edit = masterId => {
    navigate('edit', {state: {id: masterId}});
  };

  const setForDeletion = id => {
    const record = masterData.filter(record => record.id == id);
    setForDelete({id: id, recordNumber: record[0].recordNumber});
    onOpen();
  };

  const deleteMaster = async () => {
    const data = await postDelete(SMaster.DELETE, `"${forDelete.id}"`);
    setToastResponse({response: data, successMessage: 'Successfully deleted record ' + forDelete.recordNumber});
    if (!data.error) {
      loadData();
      onClose();
    }
  };
  useEffect(() => {
    setLoading(true);
    loadData(pages.pageNum);
  }, [pages]);

  const populateData = () => {
    // for the function parameters passed in we're going to use the id of data being populated, can do other stuff with it but that was best solution I could come up with
    // this id will be set in the setGrid when the data is populated... really hoping all of our data will have id's!
    const ExtraColumns = ({id}) => {
      return (
        <>
          <Td>
            <IconButton colorScheme="tertiary" title="Edit" size="md" icon={<FaEdit />} mr="2" onClick={() => edit(id)} />
            {!isSalesMan && <IconButton colorScheme="tertiary" title="Delete" size="md" icon={<FaTrashAlt />} onClick={() => setForDeletion(id)} />}
          </Td>
        </>
      );
    };
    setGrid(visibleData, masterData, pages, setElements, ExtraColumns);
    // setSearchHeaders(searchIds, setHeaders, register, errors, ascFunc, descFunc);
    setSearchHeaders(searchIds, setHeaders, register, errors);
  };

  const search = async formData => {
    // delete any empty parameters
    setLoading(true);
    formData.OrderBy = 'ContractDate';
    formData.IsDescendingSort = true;
    Object.keys(formData).forEach(k => !formData[k] && formData[k] !== undefined && delete formData[k]);
    await queryData(formData);
  };

  const queryData = async formData => {
    let url = '';
    formData.Page = 1;
    formData.PageSize = 20;
    if (isException) {
      url = queryStringBuilder(SMaster.EXCEPTION, formData);
    } else {
      url = queryStringBuilder(SMaster.LIST, formData);
    }
    const data = await get(url);
    // console.log('formData ' + JSON.stringify(formData));

    setToastResponse({response: data});
    if (!data.error) {
      if (data.data.length === 1) {
        navigate('edit', {state: {id: data.data[0].id}});
      } else {
        setMasterData(data.data);
        setLoading(false);
      }
    }
  };

  // Used both of these functions for sorting but decided to take them out for now - ALV 20220513
  // const ascFunc = orderBy => {
  //   setLoading(true);
  //   handleSubmit(data => {
  //     Object.keys(data).forEach(k => !data[k] && data[k] !== undefined && delete data[k]);
  //     data.OrderBy = orderBy;
  //     data.IsDescendingSort = false;
  //     queryData(data);
  //   })();
  // };

  // const descFunc = orderBy => {
  //   setLoading(true);
  //   handleSubmit(data => {
  //     Object.keys(data).forEach(k => !data[k] && data[k] !== undefined && delete data[k]);
  //     data.OrderBy = orderBy;
  //     data.IsDescendingSort = true;
  //     queryData(data);
  //   })();
  // };

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toastResponse} />
        </>
      ) : (
        <form onSubmit={handleSubmit(search)}>
          <BoxCard width={{base: '100%', '2xl': '95%'}}>
            <Flex width="80%">
              {/* <CustomInput register={register} name="Batch Number" id="BatchNumber" errors={errors} requireInt />
              <CustomInput register={register} name="Record Number" id="RecordNumber" errors={errors} requireInt /> */}
              <Box mt="5">
                <ServiceTypeList control={control} defaultValue={serviceType} />
              </Box>
              <Spacer />
              <SubmitButton name="Search" rightIcon={<FaSearch />} mb="2" mr="5" colorScheme="tertiary" w="25%" alignSelf="end" />
            </Flex>

            <Divider mt="5" colorScheme={'mint'} />
            <TableContainer>
              <Table variant="striped" colorScheme="mint">
                <TableCaption>{isException ? 'Exception Maintenance' : 'Master Maintenance'}</TableCaption>
                <Thead>
                  <Tr>
                    {searchHeaders}
                    <Td px="-2"></Td>
                  </Tr>
                </Thead>
                <Tbody>{elements.body}</Tbody>
                <Tfoot>{/* <Tr>{elements.footer}</Tr> */}</Tfoot>
              </Table>
            </TableContainer>
            <ModalYesNo
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              title="Delete record"
              body={'Are you sure you want to delete record ' + forDelete.recordNumber}
              yesFunction={deleteMaster}
            />
          </BoxCard>
          <ErrorHelper toastResponse={toastResponse} />
        </form>
      )}
    </>
  );
};

export default MasterMaintenance;
